/**
 * Handles the product slider functionality for the Ombraz V2 Collection PDP
 */
const COLLECTION_CARDS_EXPERIMENT_CLASS = "testgroup-collection-cards-b";

function isCollectionCardsExperimentActive() {
  return Boolean(
    document.body?.dataset.currentTemplate === "collection-new-collection" &&
      document.body.classList.contains(COLLECTION_CARDS_EXPERIMENT_CLASS),
  );
}

function getSelectedCollectionFrameSize() {
  const selectedFilter = document.querySelector(
    'input[data-filter-type="frame-size"]:checked',
  );
  const selectedSize = (
    selectedFilter?.getAttribute("data-frame-size") || ""
  )
    .trim()
    .toLowerCase();

  return ["regular", "narrow", "xl"].includes(selectedSize)
    ? selectedSize
    : "";
}

function normalizeLimitedEditionTag(value) {
  const normalizedValue = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalizedValue === "limited edition" ? "Limited Edition" : "";
}

function isCollectionLimitedEditionModeActive() {
  return Boolean(
    document.querySelector('.ombraz-limited-edition input[type="checkbox"]')
      ?.checked,
  );
}

function v2CollectionPdpSlider() {
  // Select all necessary elements within the card
  const cards = Array.from(
    document.querySelectorAll(".ombraz-product-v2__card"),
  );

  if (!cards.length) return;

  cards.forEach((card) => {
    const featuredImageElement = card.querySelector("[data-feaure-image]");
    const sideShieldToggle = card.querySelector("label input[type=checkbox]");
    const productImageUrl = card.querySelector(
      ".ombraz-product-v2__card-feature-image-link",
    );
    const productReviewUrl = card.querySelector(".klaviyo-star-rating-wrapper");
    const productCardUrl = card.querySelector(".ombraz-product-v2__card-url");
    const shopNowLink = card.querySelector("[data-collection-card-shop-now]");
    const productVariantTitle = card.querySelector(
      ".ombraz-product-v2__card-variant-title > p",
    );
    const productPrice = card.querySelector(
      ".ombraz-product-v2__current-price",
    );
    const sizeButtons = {
      regular: card.querySelector("label[for^=regular] input[type=radio]"),
      narrow: card.querySelector("label[for^=narrow] input[type=radio]"),
      xl: card.querySelector("label[for^=xl] input[type=radio]"),
    };
    const slides = Array.from(
      card.querySelectorAll(".ombraz-product-v2-slide"),
    );
    const slidesWrapper = card.querySelector(".ombraz-product-v2-slides");
    const frameSizeWrapper = card.querySelector(
      ".ombraz-product-v2__frame-size-wrapper",
    );
    const defaultSlideKey = card.getAttribute("data-default-slide-key") || "";
    const defaultCardSize = card.getAttribute("data-default-size") || "regular";

    // Only process cards that have the required elements (main product cards, not accessories)
    if (!featuredImageElement || !slides.length) return;

    const quickAddButton = card
      .closest(".ombraz-product-v2__grid-item")
      .querySelector(".ombraz-product-v2__add-to-cart button");

    // Initialize variables
    let activeSlide =
      slides.find((slide) => slide.classList.contains("is-active")) ||
      slides.find(
        (slide) => slide.getAttribute("data-slide-key") === defaultSlideKey,
      ) ||
      slides[0];
    let isSideShieldAdded = false;
    let currentSize = defaultCardSize;
    let alertShownForCurrentSlide = false;
    const sizeOrder = ["regular", "narrow", "xl"];

    const getSlideTagForSize = (slide, size) => {
      if (!slide || !size) return "";
      const explicitTag = slide.getAttribute(`data-${size}-htag`) || "";
      if (explicitTag.trim() !== "") {
        return normalizeLimitedEditionTag(explicitTag);
      }

      if ((slide.getAttribute("data-frame-size") || "").trim() === size) {
        return normalizeLimitedEditionTag(
          slide.getAttribute("data-highlight-tag") || "",
        );
      }

      return "";
    };

    const getVisibleSizesForSlide = (slide, limitedEditionMode = false) =>
      sizeOrder.filter((size) => {
        const variantId = slide?.getAttribute(`data-${size}-id`);
        if (!variantId || variantId === "null") return false;
        if (!limitedEditionMode) return true;
        return getSlideTagForSize(slide, size) !== "";
      });

    const slideSupportsLimitedEdition = (slide) =>
      getVisibleSizesForSlide(slide, true).length > 0;

    const setActiveSlide = (slide) => {
      if (!slide) return;
      slides.forEach((entry) => entry.classList.remove("is-active"));
      slide.classList.add("is-active");
      activeSlide = slide;
    };

    const syncSlideVisibilityForMode = (
      limitedEditionMode,
      selectedSize = "",
    ) => {
      const visibleSlides = slides.filter((slide) => {
        if (selectedSize) {
          const selectedVariantId = slide.getAttribute(
            `data-${selectedSize}-id`,
          );
          if (!selectedVariantId || selectedVariantId === "null") return false;
        }

        if (!limitedEditionMode) return true;

        return selectedSize
          ? getSlideTagForSize(slide, selectedSize) !== ""
          : slideSupportsLimitedEdition(slide);
      });

      slides.forEach((slide) => {
        slide.style.display = visibleSlides.includes(slide) ? "" : "none";
      });

      if (slidesWrapper) {
        slidesWrapper.style.display = visibleSlides.length ? "" : "none";
      }

      return visibleSlides;
    };

    const resolvePreferredSize = (slide, limitedEditionMode = false) => {
      const visibleSizes = getVisibleSizesForSlide(slide, limitedEditionMode);
      if (!visibleSizes.length) return "";
      if (visibleSizes.includes(currentSize)) return currentSize;
      if (visibleSizes.includes(defaultCardSize)) return defaultCardSize;
      return visibleSizes[0];
    };

    // Helper to get current frame size. Variation B is controlled exclusively
    // by the collection filter; control keeps the existing card-first behavior.
    const getCurrentFrameSize = () => {
      if (isCollectionCardsExperimentActive()) {
        return getSelectedCollectionFrameSize() || null;
      }

      // First, check THIS card's own frame size radio buttons
      const cardRadioButtons = card.querySelectorAll(
        'input[type="radio"][name^="frame-size-"]:checked',
      );

      if (cardRadioButtons.length > 0) {
        const checkedButton = cardRadioButtons[0];
        const buttonId = checkedButton.id; // e.g. "regular-12345"
        const size = buttonId.split("-")[0]; // Extract "regular", "narrow", or "xl"
        return size || null;
      }

      // Second, check sidebar filter
      const sidebarFilter = document.querySelector(
        'input[data-filter-type="frame-size"]:checked',
      );

      if (sidebarFilter) {
        const size = (
          sidebarFilter.getAttribute("data-frame-size") || ""
        ).toLowerCase();
        return size || null;
      }

      // Finally, URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const frameSizeFromUrl = urlParams.get("frame_size");
      return frameSizeFromUrl ? frameSizeFromUrl.toLowerCase() : null;
    };

    // Initialize from URL or filter
    const initialFrameSize = getCurrentFrameSize();

    if (initialFrameSize && sizeButtons[initialFrameSize]) {
      sizeButtons[initialFrameSize].checked = true;
      currentSize = initialFrameSize;
    }

    const updateAnchorWithFrameSize = (anchor, size) => {
      if (!anchor || !size) return;

      try {
        const url = new URL(anchor.href, window.location.origin);
        url.searchParams.set("frame_size", size);
        anchor.href = url.toString();
      } catch (_error) {
        // Ignore malformed URLs.
      }
    };

    const updateAllAnchorsWithFrameSize = () => {
      const size = getCurrentFrameSize();
      if (!size) return;

      updateAnchorWithFrameSize(productCardUrl, size);
      updateAnchorWithFrameSize(productImageUrl, size);
      updateAnchorWithFrameSize(productReviewUrl, size);
      updateAnchorWithFrameSize(shopNowLink, size);
    };

    const attachFrameSizeParam = (anchor) => {
      if (!anchor) return;

      const addParam = () => {
        updateAllAnchorsWithFrameSize();
      };

      anchor.addEventListener("click", addParam, { capture: true });
      anchor.addEventListener("auxclick", (event) => {
        if (event.button === 1) {
          addParam();
        }
      });
    };

    /**
     * Preloads an image to improve performance
     * @param {string} url - The URL of the image to preload
     */
    function preloadImage(url) {
      const img = new Image();
      img.src = url;
    }

    function syncQuickAddButton(
      slide,
      size,
      slideProductId,
      slideProductTitle,
    ) {
      if (!quickAddButton || !slideProductId) return;

      const isAvailable =
        slide.getAttribute(`data-is-${size}-available`) === "true";

      quickAddButton.setAttribute("data-product-id", slideProductId);

      if (card.hasAttribute("data-sideshield-id")) {
        quickAddButton.setAttribute(
          "data-side-shield-id",
          card.getAttribute("data-sideshield-id"),
        );
      }

      if (isAvailable) {
        quickAddButton.textContent = "Add to Cart";
        quickAddButton.disabled = false;
        quickAddButton.classList.remove("klaviyo-collection-bis-trigger");
        quickAddButton.style.cursor = "pointer";
        quickAddButton.style.opacity = 1;
        quickAddButton.removeAttribute("data-variant");
        quickAddButton.removeAttribute("data-product-name");
        return;
      }

      quickAddButton.textContent = "Notify when in stock";
      quickAddButton.disabled = false;
      quickAddButton.classList.add("klaviyo-collection-bis-trigger");
      quickAddButton.setAttribute("data-variant", slideProductId);

      const sizeFormatted = size.charAt(0).toUpperCase() + size.slice(1);
      quickAddButton.setAttribute(
        "data-product-name",
        `${slideProductTitle} / ${sizeFormatted}`,
      );
      quickAddButton.style.cursor = "pointer";
      quickAddButton.style.opacity = 1;
    }

    /**
     * Updates size button visibility based on the active slide's available sizes
     * Liquid template already filters out EOL variants, so if a variant ID exists in data attributes, it's not EOL
     * @param {HTMLElement} slide - The selected slide element
     */
    function updateSizeButtonVisibility(slide) {
      const limitedEditionMode = isCollectionLimitedEditionModeActive();
      const visibleSizes = getVisibleSizesForSlide(slide, limitedEditionMode);
      const experimentActive = isCollectionCardsExperimentActive();

      // Variation B shows a single read-only pill for the size picked in the
      // filter sidebar. Regular and "no size selected" show nothing.
      const selectedFilterSize = experimentActive
        ? getSelectedCollectionFrameSize()
        : "";
      const experimentPillSize =
        selectedFilterSize === "narrow" || selectedFilterSize === "xl"
          ? selectedFilterSize
          : "";

      // Variant IDs on the slide are already filtered by Liquid.
      const isSizeAvailable = (size) => {
        const variantId = slide.getAttribute(`data-${size}-id`);
        if (!variantId || variantId === "null") return false;
        return !limitedEditionMode || visibleSizes.includes(size);
      };

      sizeOrder.forEach((size) => {
        const input = sizeButtons[size];
        if (!input) return;

        const label = input.closest("label");
        const available = isSizeAvailable(size);
        const shouldShow = experimentActive
          ? available && size === experimentPillSize
          : available;

        if (shouldShow) {
          label.style.display = "";
          label.style.visibility = "visible";
        } else {
          label.style.display = "none";
        }

        // Variation B hides sizes visually only, so the filter can still drive them.
        input.disabled = experimentActive ? false : !available;
      });

      // If current size is unavailable, switch to first available size
      if (
        currentSize &&
        sizeButtons[currentSize] &&
        !isSizeAvailable(currentSize)
      ) {
        currentSize = resolvePreferredSize(slide, limitedEditionMode);
      }

      if (!currentSize || !visibleSizes.includes(currentSize)) {
        currentSize = resolvePreferredSize(slide, limitedEditionMode);
      }

      if (currentSize && sizeButtons[currentSize]) {
        sizeButtons[currentSize].checked = true;
      }

      if (frameSizeWrapper) {
        const hasVisibleLabel = experimentActive
          ? Boolean(experimentPillSize) && isSizeAvailable(experimentPillSize)
          : visibleSizes.length > 0;
        frameSizeWrapper.style.display = hasVisibleLabel ? "" : "none";
      }
    }

    /**
     * Updates the product information based on the selected slide and size
     * @param {HTMLElement} slide - The selected slide element
     * @param {string} size - The selected size (default: currentSize)
     */
    function updateProductInfo(slide, size = currentSize) {
      const limitedEditionMode = isCollectionLimitedEditionModeActive();
      const experimentSize = isCollectionCardsExperimentActive()
        ? getSelectedCollectionFrameSize()
        : "";
      if (!slide) return;

      const resolvedSize =
        resolvePreferredSize(slide, limitedEditionMode) ||
        (slide.getAttribute("data-frame-size") || "").trim() ||
        defaultCardSize;
      const experimentVariantId = experimentSize
        ? slide.getAttribute(`data-${experimentSize}-id`)
        : "";
      if (
        experimentSize &&
        experimentVariantId &&
        experimentVariantId !== "null"
      ) {
        size = experimentSize;
      } else if (!size || !slide.getAttribute(`data-${size}-id`)) {
        size = resolvedSize;
      }
      currentSize = size || resolvedSize;

      const slideProductId =
        slide.getAttribute(`data-${currentSize}-id`) ||
        slide.getAttribute("data-slide-product-id");
      const baseProductUrl = slide
        .getAttribute("data-product-url")
        .split("?")[0];
      const slideProductTitle = slide.getAttribute("data-product-title");
      const slideProductPrice = parseInt(
        slide.getAttribute(`data-${currentSize}-price`) ||
          slide.getAttribute("data-product-price"),
      );

      // Prefer the per-size compare-at; sizes can differ once SKUs are excluded from a preview discount
      const slideCompareAtPrice =
        parseInt(
          slide.getAttribute(`data-${currentSize}-compare-price`) ||
            slide.getAttribute("data-compare-at-price"),
        ) || 0;
      const compareAtPrice = card.querySelector("[data-base-compare-price]");
      const sideShieldPrice =
        parseInt(slide.getAttribute("data-sideshield-price")) || 65;

      // Calculate prices using the slide's specific price
      let currentPrice = slideProductPrice;
      let currentComparePrice = slideCompareAtPrice;

      if (isSideShieldAdded) {
        currentPrice += sideShieldPrice;
        if (currentComparePrice > 0) {
          currentComparePrice += sideShieldPrice;
        }
      }

      // Update prices
      if (productPrice) {
        productPrice.textContent = `$${currentPrice}`;
      }

      if (compareAtPrice) {
        // Only show compare price if it's greater than current price
        if (currentComparePrice > currentPrice) {
          compareAtPrice.textContent = `$${currentComparePrice}`;
          if (compareAtPrice.parentElement) {
            compareAtPrice.parentElement.style.display = "";
          }
        } else {
          if (compareAtPrice.parentElement) {
            compareAtPrice.parentElement.style.display = "none";
          }
        }
      }

      let updatedProductUrl = `${baseProductUrl}?variant=${slideProductId}`;

      // Add side shield query param if side shield is added
      if (isSideShieldAdded) {
        updatedProductUrl += "&addons=sideshields";
      }

      card.setAttribute("data-default-product-id", slideProductId);
      if (productCardUrl) productCardUrl.href = updatedProductUrl;
      if (productImageUrl) productImageUrl.href = updatedProductUrl;
      if (productReviewUrl)
        productReviewUrl.href = `${updatedProductUrl}#pdp-review-section`;
      if (shopNowLink) shopNowLink.href = updatedProductUrl;
      updateAllAnchorsWithFrameSize();

      if (productVariantTitle) {
        const displayFrame = slide.getAttribute("data-display-frame");
        const displayLens = slide.getAttribute("data-display-lens");

        if (displayFrame && displayLens) {
          productVariantTitle.innerHTML = `
            <span>${displayFrame}</span>
            <span>${displayLens}</span>
          `;
        } else {
          // Get the display title which has the format "Frame Color, Lens Type"
          const displayTitle = slide.getAttribute("data-display-title");

          if (displayTitle) {
            // Split by comma to get frame color and lens type
            const [frameColor, lensType] = displayTitle
              .split(", ")
              .map((s) => s.trim());

            productVariantTitle.innerHTML = `
              <span>${frameColor}</span>
              <span>${lensType}</span>
            `;
          }
        }
      }

      // Update Highlight Badge
      let limitedEditionTag = "";
      const badgeElem = card.querySelector(".ob-badge.ob-badge-dynamic");
      let htag = slide.getAttribute(`data-${currentSize}-htag`);
      if (htag == null || htag === "") {
        htag = slide.getAttribute("data-highlight-tag") || "";
      }
      limitedEditionTag = normalizeLimitedEditionTag(htag);

      if (badgeElem) {
        const badgeSlot = badgeElem.closest("[data-badge-slot]");
        const hasBadge = limitedEditionTag !== "";

        badgeElem.textContent = hasBadge
          ? limitedEditionTag
          : "Limited Edition";
        badgeElem.classList.toggle("is-empty", !hasBadge);

        if (badgeSlot) {
          badgeSlot.classList.toggle("is-empty", !hasBadge);
          badgeSlot.dataset.badgeVisible = hasBadge ? "true" : "false";
          if (hasBadge) {
            badgeSlot.removeAttribute("aria-hidden");
          } else {
            badgeSlot.setAttribute("aria-hidden", "true");
          }
        }

        badgeElem.style.removeProperty("display");
      }

      const gridItem = card.closest(".ombraz-product-v2__grid-item");
      if (gridItem) {
        gridItem.setAttribute(
          "data-limited-edition",
          limitedEditionTag !== "" ? "true" : "false",
        );
      }

      updateFeaturedImage(slide);

      // Update size button visibility based on this slide's available sizes
      updateSizeButtonVisibility(slide);
      syncQuickAddButton(slide, currentSize, slideProductId, slideProductTitle);
    }

    /**
     * Updates the featured image based on the selected slide and side shield status
     * @param {HTMLElement} slide - The selected slide element
     */
    function updateFeaturedImage(slide) {
      const currentSlideImageSrc =
        slide.querySelector("[data-thumb-image]")?.src;
      const sideShieldImageSrc = slide.getAttribute("data-side-shield-image");

      // Preload the images
      preloadImage(currentSlideImageSrc);
      if (sideShieldImageSrc) {
        preloadImage(sideShieldImageSrc);
      }

      // The slider is behind a theme setting, so the card may hold a plain image.
      const swiperApi = card.querySelector("[data-card-imgswiper]")
        ? window.OmbrazCardImageSwiper
        : null;
      const frontSrc =
        isSideShieldAdded && sideShieldImageSrc
          ? sideShieldImageSrc
          : currentSlideImageSrc;

      if (swiperApi) {
        // The side shot belongs to the variant, so it changes with the lens.
        // Side shields have no side shot, so that state falls back to one image.
        const sideSrc = isSideShieldAdded
          ? ""
          : slide.getAttribute("data-side-image");
        swiperApi.applyVariantImages(card, frontSrc, sideSrc);
        return;
      }

      requestAnimationFrame(() => {
        featuredImageElement.src = frontSrc;
      });
    }

    function applyCardSelectionMode({ forceReset = false } = {}) {
      const limitedEditionMode = isCollectionLimitedEditionModeActive();
      const experimentSize = isCollectionCardsExperimentActive()
        ? getSelectedCollectionFrameSize()
        : "";
      const visibleSlides = syncSlideVisibilityForMode(
        limitedEditionMode,
        experimentSize,
      );

      let nextSlide = activeSlide;

      if (forceReset || !nextSlide || !visibleSlides.includes(nextSlide)) {
        if (experimentSize || limitedEditionMode) {
          nextSlide =
            visibleSlides[0] ||
            (limitedEditionMode
              ? slides.find((slide) => slideSupportsLimitedEdition(slide))
              : null);
        } else {
          nextSlide =
            slides.find(
              (slide) =>
                slide.getAttribute("data-slide-key") === defaultSlideKey,
            ) ||
            visibleSlides[0] ||
            slides[0];
        }
      }

      if (!nextSlide) return;

      setActiveSlide(nextSlide);

      const shouldRestoreDefaultSize =
        isCollectionCardsExperimentActive() &&
        !experimentSize &&
        !limitedEditionMode &&
        forceReset;
      let preferredSize =
        experimentSize ||
        resolvePreferredSize(nextSlide, limitedEditionMode);

      if (shouldRestoreDefaultSize) {
        preferredSize = getVisibleSizesForSlide(nextSlide).includes(
          defaultCardSize,
        )
          ? defaultCardSize
          : resolvePreferredSize(nextSlide);
      }

      if (preferredSize) {
        currentSize = preferredSize;
      }

      updateSizeButtonVisibility(nextSlide);
      updateProductInfo(nextSlide, currentSize);
    }

    function updateSizeSelection(size) {
      if (isCollectionCardsExperimentActive()) {
        applyCardSelectionMode({ forceReset: true });
        return;
      }

      currentSize = size;
      updateSizeButtonVisibility(activeSlide);
      updateProductInfo(activeSlide, size);
    }

    // Add event listeners to slides
    slides.forEach((slide) => {
      slide.addEventListener("click", (event) => {
        event.stopPropagation();
        setActiveSlide(slide);
        alertShownForCurrentSlide = false; // Reset the alert flag

        // Update size button visibility first, then update product info
        updateSizeButtonVisibility(slide);
        updateProductInfo(slide, currentSize);
      });
    });

    if (sideShieldToggle) {
      sideShieldToggle.addEventListener("change", (event) => {
        isSideShieldAdded = event.target.checked;
        const sideShieldImageSrc = activeSlide?.getAttribute(
          "data-side-shield-image",
        );

        if (
          isSideShieldAdded &&
          !sideShieldImageSrc &&
          !alertShownForCurrentSlide
        ) {
          injectCustomMessage(
            "Unfortunately, the image of the side shield is currently unavailable.",
            card.querySelector(".ombraz-product-v2__price"),
            true,
          );
          alertShownForCurrentSlide = true;
        } else if (!isSideShieldAdded) {
          injectCustomMessage(
            "",
            card.querySelector(".ombraz-product-v2__price"),
            false,
          );
          alertShownForCurrentSlide = false;
        }

        updateProductInfo(activeSlide);
        updateFeaturedImage(activeSlide);
      });
    }

    Object.entries(sizeButtons).forEach(([size, button]) => {
      if (button) {
        button.addEventListener("change", () => {
          updateSizeSelection(size);
          updateAllAnchorsWithFrameSize();
        });
      }
    });

    const filterInputs = document.querySelectorAll(
      'input[data-filter-type="frame-size"]',
    );

    filterInputs.forEach((input) => {
      input.addEventListener("change", () => {
        const newSize = getCurrentFrameSize();

        if (newSize && sizeButtons[newSize]) {
          currentSize = newSize;
        }

        applyCardSelectionMode({
          forceReset: isCollectionCardsExperimentActive(),
        });
        updateAllAnchorsWithFrameSize();
      });
    });

    attachFrameSizeParam(productCardUrl);
    attachFrameSizeParam(productImageUrl);
    attachFrameSizeParam(productReviewUrl);
    attachFrameSizeParam(shopNowLink);

    document.addEventListener("ombraz:limited-edition-mode-change", (event) => {
      applyCardSelectionMode({
        forceReset: Boolean(event?.detail?.active),
      });
    });

    document.addEventListener("ombraz:collection-frame-size-change", () => {
      applyCardSelectionMode({
        forceReset: isCollectionCardsExperimentActive(),
      });
      updateAllAnchorsWithFrameSize();
    });

    document.addEventListener(
      "ombraz:collection-card-experiment-change",
      () => {
        applyCardSelectionMode({
          forceReset: isCollectionCardsExperimentActive(),
        });
        updateAllAnchorsWithFrameSize();
      },
    );

    // Handle Add to Cart button click
    if (quickAddButton) {
      quickAddButton.addEventListener("click", () => {
        // Check if this is a Klaviyo back-in-stock trigger
        if (
          quickAddButton.classList.contains("klaviyo-collection-bis-trigger")
        ) {
          // Let the Klaviyo handler take care of this
          return;
        }

        const productId = quickAddButton.getAttribute("data-product-id");
        const sideShieldId = quickAddButton.hasAttribute("data-side-shield-id")
          ? quickAddButton.getAttribute("data-side-shield-id")
          : null;
        const items = [
          {
            id: parseInt(productId),
            quantity: 1,
          },
        ];

        if (isSideShieldAdded && sideShieldId) {
          items.push({
            id: parseInt(sideShieldId),
            quantity: 1,
          });
        }

        addToCart(items, quickAddButton);
      });
    }

    applyCardSelectionMode({
      forceReset: isCollectionLimitedEditionModeActive(),
    });
  });
}

/**
 * Handles simple product cards that don't require the full slider functionality
 * Only handles available products, not OOS products (which are handled by Klaviyo BIS)
 */
function handleSimpleProductCards() {
  const simpleCards = Array.from(
    document.querySelectorAll(".ombraz-product-v2__card"),
  );

  if (!simpleCards.length) return;

  simpleCards.forEach((card) => {
    // Only select buttons that are NOT klaviyo-collection-bis-trigger buttons
    const quickAddButton = card.querySelector(
      ".ombraz-product-v2__add-to-cart button:not(.klaviyo-collection-bis-trigger)",
    );

    if (!quickAddButton) return;

    // Double-check that this is NOT a Klaviyo BIS button
    if (quickAddButton.classList.contains("klaviyo-collection-bis-trigger")) {
      console.log("Skipping Klaviyo BIS button in simple cards handler");
      return;
    }

    const productId = card.getAttribute("data-product-id");

    quickAddButton.addEventListener("click", function () {
      if (this.classList.contains("klaviyo-collection-bis-trigger")) {
        // Prevent SLIDE CART from opening
        window.SLIDECART_OPENED = false;
        console.log("PREVENTED SLIDE CART");
        return;
      }

      const items = [
        {
          id: parseInt(productId),
          quantity: 1,
        },
      ];

      addToCart(items, quickAddButton);
    });
  });
}

/**
 * Injects or removes a custom message near the target element
 * @param {string} message - The message to inject
 * @param {HTMLElement} targetElement - The element to inject the message near
 * @param {boolean} shouldInject - Whether to inject or remove the message
 */
function injectCustomMessage(message, targetElement, shouldInject) {
  const existingMessage = targetElement.parentNode.querySelector(
    ".custom-alert-message",
  );

  if (shouldInject) {
    // If the message does not exist, create and inject it
    if (!existingMessage) {
      const newDiv = document.createElement("div");
      newDiv.className = "custom-alert-message";
      newDiv.textContent = message;

      // Insert the new div before the target element
      if (targetElement) {
        targetElement.parentNode.insertBefore(newDiv, targetElement);
      }
    }
  } else {
    // Remove the existing message if checkbox is unchecked
    if (existingMessage) {
      existingMessage.remove();
    }
  }
}

/**
 * Capitalizes the first letter of a string
 * @param {string} str - The string to capitalize
 * @returns {string} The capitalized string
 */
function toCapitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Adds items to the cart and updates the UI accordingly
 * @param {Array} items - An array of items to add to the cart
 * @param {HTMLElement} quickAddButton - The Add to Cart button element
 */
function addToCart(items, quickAddButton) {
  if (quickAddButton) {
    quickAddButton.textContent = "Adding item...";
    quickAddButton.disabled = true;
    quickAddButton.style.cursor = "not-allowed";
    quickAddButton.style.opacity = 0.5;
  }

  const formData = {
    items: items,
  };

  fetch(window.Shopify.routes.root + "cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(formData),
  })
    .then((response) => response.json())
    .then((_data) => {
      setTimeout(() => {
        if (quickAddButton) {
          quickAddButton.textContent = "Added!";
          quickAddButton.disabled = false;
          quickAddButton.style.cursor = "pointer";
          quickAddButton.style.opacity = 1;
          const cartCount = document.querySelector(".header-new__cart-count");
          if (cartCount) {
            cartCount.textContent = "";
            cartCount.textContent = _data?.items?.length;
          }

          setTimeout(() => {
            if (quickAddButton) {
              quickAddButton.textContent = "Add to Cart";
            }
          }, 2000);
          SLIDECART_UPDATE();
          SLIDECART_OPEN();
        }
      }, 1000);
    })
    .catch((_error) => {
      if (quickAddButton) {
        quickAddButton.textContent = "Adding item...";
        quickAddButton.disabled = false;
        quickAddButton.style.cursor = "pointer";
        quickAddButton.style.opacity = 1;

        setTimeout(() => {
          if (quickAddButton) {
            quickAddButton.textContent = "Add to Cart";
          }
        }, 3000);
      }
    });
}

/**
 * Handles accessory product cards (sideshield, riser, gear, etc.)
 * Only handles available products with 'accessory-quick-add-btn' class
 */
function handleAccessoryProductCards() {
  // Only select buttons that have EXACTLY the accessory-quick-add-btn class
  const accessoryButtons = Array.from(
    document.querySelectorAll("button.accessory-quick-add-btn"),
  );

  if (!accessoryButtons.length) return;

  console.log(
    `Found ${accessoryButtons.length} available accessory buttons to handle`,
  );

  accessoryButtons.forEach((button) => {
    // Double-check that this is NOT a Klaviyo BIS button
    if (button.classList.contains("klaviyo-collection-bis-trigger")) {
      console.log("Skipping Klaviyo BIS button in accessory handler");
      return;
    }

    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();

      console.log(
        "Available accessory button clicked:",
        button.getAttribute("data-accessory-type"),
      );

      const variantId =
        button.getAttribute("data-variant-id") ||
        button.getAttribute("data-product-id");

      if (!variantId) {
        console.error("Error: Variant ID is missing for accessory product.");
        return;
      }

      const items = [
        {
          id: parseInt(variantId),
          quantity: 1,
        },
      ];

      addToCart(items, button);
    });
  });
}

// Initialize the functionality when the DOM is fully loaded
document.addEventListener("DOMContentLoaded", () => {
  v2CollectionPdpSlider();
  handleSimpleProductCards();
  handleAccessoryProductCards();
});
